import { DailyMapOrder } from "../models/orders/daily-map";

export type DailyMapFilter = "total" | "assigned" | "delivered" | "pending" | "cancelled" | "delayed" | "unknown";
export type DailyMapBucket = "delivered" | "pending" | "notDelivered" | "cancelled" | "delayed" | "unknown";
export const dailyOrderKey = (order: DailyMapOrder) => `${order.driverID}|${order.orderID}`;
export const distinctDailyOrders = (orders: DailyMapOrder[]) => Array.from(new Map(orders.map(o => [dailyOrderKey(o), o])).values());
// The existing bulk Delay Orders endpoint records Delivery Time Out as status 4.
const isReasonedDelay = (order: DailyMapOrder) => !!order.delayedStatusId && order.delayedStatusId !== 4;
export const dailyMapDelayReason = (order: DailyMapOrder) => order.delayedStatusName?.trim() ||
  (order.delayedStatusId === 4 ? "Delivery Time Out" : order.delayedStatusId ? "Reason unavailable" : "—");

export function dailyMapBucket(order: DailyMapOrder): DailyMapBucket {
  // Saved assessment outcomes take precedence over later invoices, cancellations and delays.
  if (order.isHistorical) {
    switch (order.outcome) {
      case "Delivered": return "delivered";
      case "NotDelivered": return isReasonedDelay(order) ? "delayed" : "notDelivered";
      case "Cancelled": return "cancelled";
      case "Pending": return isReasonedDelay(order) ? "delayed" : "pending";
      default: return "unknown";
    }
  }
  if (order.isInvoiced) return "delivered";
  if (order.isCancelled || order.totalReturnOrCancelled) return "cancelled";
  return isReasonedDelay(order) ? "delayed" : "pending";
}

export function matchesDailyMapFilter(order: DailyMapOrder, filter: DailyMapFilter): boolean {
  const bucket = dailyMapBucket(order);
  if (filter === "total" || filter === "assigned") return true;
  if (filter === "pending") return bucket === "pending" || bucket === "notDelivered";
  return bucket === filter;
}

export function dailyMapStatus(order: DailyMapOrder): string {
  const bucket = dailyMapBucket(order);
  if (order.delayedStatusId && ["delayed", "pending", "notDelivered"].includes(bucket))
    return `Delayed — ${dailyMapDelayReason(order)}${order.isReleased ? " — returned for replanning" : ""}`;
  if (order.isHistorical) return order.status || (order.outcome === "NotDelivered"
    ? order.isReleased ? "Not delivered — returned for replanning" : "Not delivered on planned date"
    : order.outcome === "Delivered" ? "Delivered on planned date" : order.outcome === "Cancelled"
    ? "Cancelled" : order.outcome === "Pending" ? "Pending outcome" : "Historical outcome unknown");
  return bucket === "delivered" ? "Delivered" : bucket === "cancelled" ? "Cancelled" :
    bucket === "delayed" ? order.delayedStatusName || "Delayed" : "Yet to be delivered";
}

export function dailyMapTotals(orders: DailyMapOrder[]) {
  const count = (filter: DailyMapFilter) => orders.filter(o => matchesDailyMapFilter(o, filter)).length;
  const totalCancelled = orders.filter(o => dailyMapBucket(o) === "cancelled").length;
  const totaldelayed = orders.filter(o => dailyMapBucket(o) === "delayed").length;
  return { totalOrders: orders.length, totalDelivered: count("delivered"), totalPending: count("pending"),
    totalCancelled, totaldelayed, totalCancelled1: totalCancelled, totalUnknown: count("unknown") };
}

export function dailyMapDrivers(orders: DailyMapOrder[]) {
  const groups = new Map<number, DailyMapOrder[]>();
  orders.forEach(o => {
    if (!o.driverID) return;
    if (!groups.has(o.driverID)) groups.set(o.driverID, []);
    groups.get(o.driverID)!.push(o);
  });
  return Array.from(groups, ([driverID, rows]) => {
    const totals = dailyMapTotals(rows);
    return { driverID, driverName: rows[0].driverName || "—", driverNumber: rows[0].driverNumber || "—",
      assigned: totals.totalOrders, delivered: totals.totalDelivered, pending: totals.totalPending,
      cancelled: totals.totalCancelled, totaldelayed: totals.totaldelayed, unknown: totals.totalUnknown,
      color: rows[0].color || "#3b82f6" };
  });
}
